import { supabase } from '@/lib/supabase';
import { WarrantyClaim, Laptop, PaginatedResult } from '@/types/models';
import { PAGE_SIZE } from '@/lib/constants';

export interface FetchWarrantyClaimsParams {
  search?: string;
  status?: string;
  page?: number;
}

export async function fetchWarrantyClaims(
  params: FetchWarrantyClaimsParams
): Promise<PaginatedResult<WarrantyClaim>> {
  const { search = '', status = '', page = 0 } = params;
  const from = page * PAGE_SIZE;
  const to = from + PAGE_SIZE - 1;

  let query = supabase
    .from('warranty_claims')
    .select(
      '*, laptop:laptops(id, stock_id, brand, model, serial_number, warranty_expires), sale:sales(id, invoice_number, sale_date), customer:customers(id, name, phone)',
      { count: 'exact' }
    )
    .order('opened_at', { ascending: false })
    .range(from, to);

  if (search.trim()) {
    const s = search.trim();
    query = query.or(`claim_number.ilike.%${s}%,issue.ilike.%${s}%,resolution.ilike.%${s}%`);
  }

  if (status) query = query.eq('status', status);

  const { data, error, count } = await query;
  if (error) throw error;

  return { data: (data ?? []) as WarrantyClaim[], count: count ?? 0 };
}

export async function fetchWarrantyClaimById(id: string): Promise<WarrantyClaim> {
  const { data, error } = await supabase
    .from('warranty_claims')
    .select(
      '*, laptop:laptops(*), sale:sales(*), customer:customers(*)'
    )
    .eq('id', id)
    .single();

  if (error) throw error;
  return data as WarrantyClaim;
}

export async function findSoldLaptopForWarranty(queryStr: string): Promise<Laptop[]> {
  if (!queryStr.trim()) return [];
  const s = queryStr.trim();

  const { data, error } = await supabase
    .from('laptops')
    .select('*, customer:customers!laptops_sold_to_customer_id_fkey(*)')
    .or(`stock_id.ilike.%${s}%,serial_number.ilike.%${s}%,brand.ilike.%${s}%,model.ilike.%${s}%`)
    .limit(20);

  if (error) return [];
  return (data ?? []) as Laptop[];
}

export async function createWarrantyClaim(payload: {
  laptop_id: string;
  sale_id?: string | null;
  customer_id?: string | null;
  issue: string;
  status?: string;
}): Promise<WarrantyClaim> {
  const { data, error } = await supabase
    .from('warranty_claims')
    .insert({
      laptop_id: payload.laptop_id,
      sale_id: payload.sale_id ?? null,
      customer_id: payload.customer_id ?? null,
      issue: payload.issue,
      status: payload.status ?? 'open',
    })
    .select()
    .single();

  if (error) throw error;
  return data as WarrantyClaim;
}

export async function updateWarrantyClaim(
  id: string,
  payload: {
    status?: string;
    resolution?: string | null;
  }
): Promise<WarrantyClaim> {
  const updateData: any = { ...payload };
  if (payload.status === 'closed' || payload.status === 'replaced' || payload.status === 'rejected') {
    updateData.closed_at = new Date().toISOString();
  }

  const { data, error } = await supabase
    .from('warranty_claims')
    .update(updateData)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as WarrantyClaim;
}
